import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchComparables } from '$lib/api/client';
import type { ComparableDTO, ComparablesResponse } from '$lib/api/types';

// UX decision 10: excluding a comparable triggers a recompute that lasts
// 100–500ms. The panel needs:
//   - a 1px progress bar at the top of the aggregate panel,
//   - desaturated ("stale") values until the new aggregates land,
//   - an inline "Excluded 1 comparable [Undo]" affordance with a 4-second
//     auto-dismiss (toast cadence, but inline rather than floating).
//
// The hook centralises all of that so the panel stays declarative and tests
// can assert against the state machine instead of pixels.

export const UNDO_DISMISS_MS = 4000;

export interface PendingExclusion {
  /** The comparable that was just excluded. Used to render the undo strip. */
  comparable: ComparableDTO;
  /** Wall-clock timestamp when the exclusion started, for telemetry. */
  excludedAt: number;
}

export interface UseComparablesArgs {
  unitId: string;
  /** Disable the query (e.g. while the unit-detail outer fetch is pending). */
  enabled?: boolean;
}

export interface UseComparablesResult {
  data: ComparablesResponse | undefined;
  /** True while the query is fetching for the first time. */
  isPending: boolean;
  /** True when *any* fetch is in flight (initial or recompute). */
  isFetching: boolean;
  /** True when the displayed aggregates don't yet reflect the latest exclude set. */
  isStale: boolean;
  isError: boolean;
  error: unknown;
  /** Sorted, ranked comparables that are currently *included*. */
  included: ComparableDTO[];
  /** Comparables the analyst has excluded from the current view. */
  excluded: ComparableDTO[];
  excludeIds: string[];
  pendingExclusion: PendingExclusion | null;
  exclude: (comparable: ComparableDTO) => void;
  undo: () => void;
  clearExclusions: () => void;
}

export function useComparables({ unitId, enabled = true }: UseComparablesArgs): UseComparablesResult {
  const [excludeIds, setExcludeIds] = useState<string[]>([]);
  const [pending, setPending] = useState<PendingExclusion | null>(null);
  const dismissTimerRef = useRef<number | null>(null);

  // Reset state when the target changes — different unit means a fresh
  // exclude set; carrying over would be confusing and quietly wrong.
  useEffect(() => {
    setExcludeIds([]);
    setPending(null);
    if (dismissTimerRef.current != null) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, [unitId]);

  const query = useQuery({
    queryKey: ['comparables', unitId, [...excludeIds].sort()],
    queryFn: () => fetchComparables(unitId, { excludeIds }),
    enabled: Boolean(unitId) && enabled
  });

  const exclude = useCallback((comparable: ComparableDTO) => {
    setExcludeIds((prev) => (prev.includes(comparable.id) ? prev : [...prev, comparable.id]));
    setPending({ comparable, excludedAt: Date.now() });
    if (dismissTimerRef.current != null) {
      window.clearTimeout(dismissTimerRef.current);
    }
    dismissTimerRef.current = window.setTimeout(() => {
      setPending(null);
      dismissTimerRef.current = null;
    }, UNDO_DISMISS_MS);
  }, []);

  const undo = useCallback(() => {
    setPending((cur) => {
      if (!cur) return null;
      setExcludeIds((prev) => prev.filter((id) => id !== cur.comparable.id));
      return null;
    });
    if (dismissTimerRef.current != null) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const clearExclusions = useCallback(() => {
    setExcludeIds([]);
    setPending(null);
    if (dismissTimerRef.current != null) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  // Cancel any pending dismiss timer on unmount so we don't leak setState
  // into a torn-down tree.
  useEffect(() => {
    return () => {
      if (dismissTimerRef.current != null) {
        window.clearTimeout(dismissTimerRef.current);
      }
    };
  }, []);

  const allComparables = query.data?.comparables ?? [];
  const included = allComparables.filter((c) => !excludeIds.includes(c.id));
  const excluded = allComparables.filter((c) => excludeIds.includes(c.id));

  return {
    data: query.data ?? undefined,
    isPending: query.isPending,
    isFetching: query.isFetching,
    // The aggregates rendered now reflect the *previous* exclude set whenever
    // a fetch is in flight after the user excluded something. We expose that
    // separately from `isFetching` so the panel can apply the stale class
    // only during a recompute, not during the initial load.
    isStale: query.isFetching && !query.isPending,
    isError: query.isError,
    error: query.error,
    included,
    excluded,
    excludeIds,
    pendingExclusion: pending,
    exclude,
    undo,
    clearExclusions
  };
}
